/* eslint-disable @typescript-eslint/no-shadow */
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { useEffect, useRef } from 'react';
import { components, ControlProps } from 'react-select';
import { uniqueId, debounce } from 'lodash';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { styled } from '@mui/system';
import CustomSelect from '../../../components/CustomSelect';
import { useLazyGetAreaByNameQuery } from '../../../slices/area/apis/areaApi';
import type { AreaResult } from '../../../slices/area/apis';
import { clearPoints } from '../../../slices/area/slices/searchFlightFormSlice';
import { useAppDispatch, useAppSelector } from '../../../utils/hook';
import SearchFlightResultItem from '../SearchFlightResultItem';
import type { IFlightResultItem } from '../SearchFlightResultItem/index.d';
import type { SearchInputDialogProps } from './index.d';
import { setCurrentArea } from '../../../slices/area/slices/areaSlice';
import { style } from '@/styles/globals';
import { NoResultSearchIcon, SearchIcon } from 'public/icons';
import { pr } from '@/utils/pxToRem';

const StyleLoading = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: 50%;
  align-items: center;
`;
const StyledStack = styled(Stack)`
  .reactSelectControl {
    border-color: transparent;
    border-radius: 47px;
    background-color: ${style.color.white2};
    padding: 8px 12px;
  }
  .reactSelectControl > div:first-of-type > div:first-of-type {
    color: black;
  }
`;

function transformAreaData(areas: AreaResult[]): IFlightResultItem[] {
  let dataResponse: IFlightResultItem[] = [];
  dataResponse = areas.map((area) => ({
    id: area.id,
    location: area.name,
    country: 'Việt Nam',
    highlight: area.highlight,
    type: area.type,
    stateId: area.state_id,
    stateName: area.state_name,
    locationToken: area.name_token,
    airports: area.airports
      ? area.airports.map((airport) => ({
        id: airport.id,
        nameVn: airport.properties.AirportName_Vi,
        nameEn: airport.properties.AirportName_En,
        nameShort: area.name_token,
        distance: airport.distance,
        airportCode: airport.properties.IATA_FAA,
        highlight: airport.highlight || airport.properties.AirportName_Vi,
      }))
      : [],
  }));
  return dataResponse;
}
const Control = ({ children, ...props }: ControlProps<any, true>) => (
  <components.Control
    {...props}
    className="reactSelectControl"
  >
    <SearchIcon />
    {children}
  </components.Control>
);

const SearchInputDialog: React.FC<SearchInputDialogProps> = ({
  type,
  setIsInputEmpty,
  setClose,
  lang,
}) => {
  const dispatch = useAppDispatch();
  const { startPointHome, toPointHome } = useAppSelector((state) => state.searchFlightForm);
  const areaPoint = type === 'from' ? startPointHome : toPointHome;

  const isInputEmptyRef = useRef(true);

  // const areaPoint = useAppSelector((state) => (type === 'from'
  //   ? state.searchFlightForm.startPoint
  //   : state.searchFlightForm.toPoint));

  const dataMapping = useRef<IFlightResultItem[]>([]);
  const [getAreaByName, {
    data, isFetching, error,
  }] = useLazyGetAreaByNameQuery();

  const debounceGetArea = useRef(debounce(getAreaByName, 200));
  const onExit = () => {
    dispatch(setCurrentArea({ currentArea: '' }));
    isInputEmptyRef.current = true;
    setIsInputEmpty(true);
  };
  dataMapping.current = data && (!isInputEmptyRef.current || areaPoint)
    ? transformAreaData(data)
    : [];

  const handleInputChange = (value: string) => {
    if (value.length === 0) {
      isInputEmptyRef.current = true;
      setIsInputEmpty(true);
      return;
    } if (value.length > 50) {
      return;
    }
    if (isInputEmptyRef.current) {
      setIsInputEmpty(false);
    }
    isInputEmptyRef.current = false;
    debounceGetArea.current(value, true);
  };

  useEffect(() => {
    if (!areaPoint) {
      setIsInputEmpty(true);
      isInputEmptyRef.current = true;
    }
  }, [areaPoint]);

  useEffect(() => {
    if (areaPoint) {
      getAreaByName(areaPoint.location, true);
    }
  }, []);
  return (
    <StyledStack>
      <CustomSelect
        value={null}
        Control={Control}
        locationType={type}
        onExit={onExit}
        onChange={(newValue, actionMeta) => {
          if (actionMeta.action === 'clear') {
            dispatch(clearPoints({ type }));
            isInputEmptyRef.current = true;
          }
        }}
        onInputChange={(value) => handleInputChange(value)}
        lang={lang}
      />

      {error && 'status' in error && <div>{(error.data as any).message}</div>}

      {!isInputEmptyRef.current && !isFetching
        && dataMapping.current.map((item) => (
          <SearchFlightResultItem
            type={type}
            setClose={setClose}
            isSearchResult
            key={uniqueId()}
            data={item}
            lang={lang}
            // TODO: SearchFlightResultItem
          />
        ))}
      {isFetching && (
        <StyleLoading>
          <Box sx={{
            display: 'flex',
            pb: pr`16px`,
          }}
          >
            <CircularProgress color="inherit" />
          </Box>
          <Typography variant="body2">
            {lang?.SearchBar?.flightsTab?.waiting || 'Vui lòng đợi. Tìm kiếm của bạn đang được xử lý'}
          </Typography>
        </StyleLoading>
      )}
      {data?.length === 0 && !isInputEmptyRef.current && !isFetching && (
        <StyleLoading>
          <Box sx={{
            display: 'flex',
            pb: pr`16px`,
          }}
          >
            <NoResultSearchIcon />
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: style.color.black2,
              fontWeight: '700',
            }}
          >
            {lang?.SearchBar?.flightsTab?.notFoundMobile || 'Rất tiếc Goyolo không tìm thấy địa điểm này'}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: style.color.black2,
              fontSize: '12px',
              lineHeight: '16px',
              mt: '8px',
            }}
          >
            {lang?.SearchBar?.flightsTab?.enterPlaceHolder || 'Bạn vui lòng nhập tỉnh/thành phố, quận/huyện, sân bay để tìm.'}
          </Typography>
        </StyleLoading>
      )}
    </StyledStack>
  );
};

export default SearchInputDialog;
